import { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { apiService } from '../services/api';
import { Category, MarketingBrand, PublicForm, PublicFormField } from '../types';
import { ContactLabelSelector } from '../components/ContactLabelSelector';

type ViewMode = 'list' | 'editor' | 'answers';

const makeField = (overrides: Partial<PublicFormField> = {}): PublicFormField => ({
  id: `campo_${Date.now()}_${Math.random().toString(16).slice(2)}`,
  label: 'Nova pergunta',
  type: 'text',
  required: false,
  contactField: null,
  options: [],
  ...overrides,
});

const emptyForm = (): Partial<PublicForm> => ({
  title: 'Novo formulario',
  status: 'draft',
  fields: [
    makeField({ id: 'nome', label: 'Nome', required: true, contactField: 'nome' }),
    makeField({ id: 'telefone', label: 'WhatsApp', type: 'phone', required: true, contactField: 'telefone' }),
    makeField({ label: 'Qual sua principal necessidade?', type: 'textarea', required: true, contactField: 'observacoes' }),
  ],
  buttonText: 'Enviar',
  successMessage: 'Recebemos suas informacoes.',
  defaultTags: [],
  requirePhone: true,
  theme: {},
});

function themeFromBrand(brand?: MarketingBrand | null) {
  const vt: any = brand?.visualTokens || {};
  return {
    primaryColor: vt.primaryColor || vt.primary || vt.colors?.primary || '#2563eb',
    backgroundColor: vt.backgroundColor || vt.background || '#f8fafc',
    textColor: vt.textColor || vt.text || '#0f172a',
    fontFamily: vt.fontFamily || vt.font || 'Inter, system-ui, sans-serif',
    logoUrl: brand?.logoUrl || null,
    brandName: brand?.name,
  };
}

function FormPreview({ form }: { form: Partial<PublicForm> }) {
  const theme = form.theme || {};
  const primary = theme.primaryColor || '#2563eb';
  return (
    <div className="rounded-lg border border-slate-200 p-4 shadow-sm" style={{ fontFamily: theme.fontFamily, background: theme.backgroundColor || '#f8fafc', color: theme.textColor || '#0f172a' }}>
      <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
        {theme.logoUrl && <img src={theme.logoUrl} alt="logo" className="mb-4 h-12 max-w-[180px] object-contain" />}
        <h2 className="text-xl font-semibold leading-tight">{form.title || 'Formulario de contato'}</h2>
        {form.description && <p className="mt-2 text-sm text-slate-600">{form.description}</p>}
        <div className="mt-5 space-y-4">
          {(form.fields || []).map((field) => (
            <label key={field.id} className="block text-sm font-medium">
              {field.label || 'Pergunta'}{field.required ? ' *' : ''}
              {field.type === 'textarea' ? <textarea className="mt-1 h-24 w-full rounded-md border border-slate-300 bg-white px-3 py-2" /> : null}
              {field.type === 'select' ? (
                <select className="mt-1 w-full rounded-md border border-slate-300 bg-white px-3 py-2">
                  <option>Selecione</option>
                  {(field.options || []).filter(Boolean).map((option) => <option key={option}>{option}</option>)}
                </select>
              ) : null}
              {!['textarea', 'select'].includes(field.type) ? <input className="mt-1 w-full rounded-md border border-slate-300 bg-white px-3 py-2" type={field.type === 'phone' ? 'tel' : field.type} /> : null}
            </label>
          ))}
        </div>
        <button className="mt-6 w-full rounded-md px-4 py-3 text-sm font-semibold text-white" style={{ background: primary }}>{form.buttonText || 'Enviar'}</button>
      </div>
    </div>
  );
}

export function FormsPage() {
  const [forms, setForms] = useState<PublicForm[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [brands, setBrands] = useState<MarketingBrand[]>([]);
  const [editing, setEditing] = useState<Partial<PublicForm>>(emptyForm());
  const [mode, setMode] = useState<ViewMode>('list');
  const [categorySearch, setCategorySearch] = useState('');
  const [answerData, setAnswerData] = useState<{ fields: PublicFormField[]; submissions: any[] }>({ fields: [], submissions: [] });

  const brand = brands[0] || null;
  const publicLink = useMemo(() => editing.publicUrl ? `${window.location.origin}${editing.publicUrl}` : '', [editing.publicUrl]);
  const filteredCategories = categories.filter((category: any) => {
    const term = categorySearch.trim().toLowerCase();
    if (!term) return true;
    return String(category.nome || '').toLowerCase().includes(term) || String(category.funnel?.nome || 'Sem funil').toLowerCase().includes(term);
  });
  const groupedCategories = filteredCategories.reduce<Record<string, { name: string; items: Category[] }>>((acc, category: any) => {
    const key = category.funnelId || 'sem-funil';
    if (!acc[key]) acc[key] = { name: category.funnel?.nome || 'Sem funil', items: [] };
    acc[key].items.push(category);
    return acc;
  }, {});

  async function load() {
    const [formList, categoryList, brandResult] = await Promise.all([
      apiService.listForms(),
      apiService.getAllCategories(),
      apiService.getMarketingBrands().catch(() => ({ brands: [] })),
    ]);
    const tenantBrands = brandResult.brands || [];
    setForms(formList);
    setCategories(categoryList);
    setBrands(tenantBrands);
    if (!editing.id && tenantBrands[0]) setEditing((prev) => ({ ...prev, brandId: tenantBrands[0].id, theme: themeFromBrand(tenantBrands[0]) }));
  }

  useEffect(() => { load().catch((e) => toast.error(e.message)); }, []);

  async function loadAnswers(form: PublicForm) {
    setEditing(form);
    setMode('answers');
    const data = await apiService.getFormSubmissions(form.id);
    setAnswerData(data as any);
  }

  function newForm() {
    setEditing({ ...emptyForm(), brandId: brand?.id || null, theme: themeFromBrand(brand) });
    setAnswerData({ fields: [], submissions: [] });
    setMode('editor');
  }

  function editForm(form: PublicForm) {
    setEditing(form);
    setAnswerData({ fields: [], submissions: [] });
    setMode('editor');
  }

  function updateField(index: number, patch: Partial<PublicFormField>) {
    const fields = [...(editing.fields || [])];
    fields[index] = { ...fields[index], ...patch };
    setEditing({ ...editing, fields });
  }

  function updateFieldOption(fieldIndex: number, optionIndex: number, value: string) {
    const field = (editing.fields || [])[fieldIndex];
    if (!field) return;
    const options = [...(field.options || [])];
    options[optionIndex] = value;
    updateField(fieldIndex, { options });
  }

  function addFieldOption(fieldIndex: number) {
    const field = (editing.fields || [])[fieldIndex];
    if (!field) return;
    updateField(fieldIndex, { options: [...(field.options || []), ''] });
  }

  function removeFieldOption(fieldIndex: number, optionIndex: number) {
    const field = (editing.fields || [])[fieldIndex];
    if (!field) return;
    updateField(fieldIndex, { options: (field.options || []).filter((_, index) => index !== optionIndex) });
  }

  async function save(status = editing.status || 'draft') {
    const payload = { ...editing, status, brandId: brand?.id || editing.brandId || null, theme: brand ? themeFromBrand(brand) : editing.theme };
    const saved = editing.id ? await apiService.updateForm(editing.id, payload) : await apiService.createForm(payload);
    setEditing(saved);
    await load();
    toast.success('Formulario salvo');
  }

  async function removeForm(formId = editing.id) {
    if (!formId || !confirm('Excluir este formulario?')) return;
    await apiService.deleteForm(formId);
    setEditing(emptyForm());
    setMode('list');
    await load();
  }

  async function copyLink(form: PublicForm) {
    const link = `${window.location.origin}${form.publicUrl}`;
    await navigator.clipboard.writeText(link);
    toast.success('Link copiado');
  }

  return (
    <div className="min-w-0 p-5 lg:p-6">
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Formularios</h1>
          <p className="text-sm text-slate-500">Gerencie formularios publicos, respostas e contatos capturados.</p>
        </div>
        {mode === 'list' ? <button onClick={newForm} className="rounded-md bg-slate-900 px-4 py-2 text-sm text-white">Novo formulario</button> : <button onClick={() => setMode('list')} className="rounded-md border border-slate-300 px-4 py-2 text-sm text-slate-700">Voltar para lista</button>}
      </div>

      {mode === 'list' && (
        <div className="rounded-lg border border-slate-200 bg-white">
          <div className="hidden md:grid md:grid-cols-[minmax(220px,1fr)_110px_120px_260px] gap-3 border-b border-slate-200 px-4 py-3 text-xs font-semibold uppercase tracking-wide text-slate-500">
            <span>Formulario</span><span>Status</span><span>Respostas</span><span>Acoes</span>
          </div>
          {forms.length === 0 ? <div className="p-6 text-sm text-slate-500">Nenhum formulario criado ainda.</div> : null}
          {forms.map((form) => (
            <div key={form.id} className="flex flex-col gap-2 md:grid md:grid-cols-[minmax(220px,1fr)_110px_120px_260px] md:items-center md:gap-3 border-b border-slate-100 px-4 py-3 text-sm last:border-b-0">
              <div className="min-w-0">
                <div className="truncate font-semibold text-slate-900">{form.title}</div>
                <div className="truncate text-xs text-slate-500">{`${window.location.origin}${form.publicUrl}`}</div>
              </div>
              <span className={`w-fit rounded-full px-2 py-1 text-xs ${form.status === 'published' ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-600'}`}>{form.status === 'published' ? 'Publicado' : 'Rascunho'}</span>
              <span className="text-slate-700">{form._count?.submissions || 0}</span>
              <div className="flex flex-wrap gap-2">
                <button onClick={() => editForm(form)} className="rounded-md border border-slate-300 px-3 py-1.5 text-xs">Editar</button>
                <button onClick={() => loadAnswers(form).catch((e) => toast.error(e.message))} className="rounded-md border border-slate-300 px-3 py-1.5 text-xs">Respostas</button>
                <button onClick={() => copyLink(form)} className="rounded-md border border-slate-300 px-3 py-1.5 text-xs">Copiar link</button>
                <button onClick={() => removeForm(form.id)} className="rounded-md border border-red-200 px-3 py-1.5 text-xs text-red-600">Excluir</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {mode === 'editor' && (
        <div className="grid min-w-0 gap-5 xl:grid-cols-[minmax(0,1fr)_390px]">
          <section className="min-w-0 rounded-lg border border-slate-200 bg-white p-4 lg:p-5">
            <div className="mb-5 flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 pb-4">
              <div><h2 className="text-lg font-semibold text-slate-900">{editing.id ? 'Editar formulario' : 'Criar formulario'}</h2><p className="text-sm text-slate-500">Configure perguntas, destino no funil e link de redirecionamento.</p></div>
              {publicLink && <a className="text-sm text-blue-600" href={publicLink} target="_blank">Abrir formulario publico</a>}
            </div>
            <div className="space-y-5">
              <div className="grid gap-3 md:grid-cols-2">
                <input className="min-w-0 rounded-md border border-slate-300 p-2" value={editing.title || ''} onChange={(e) => setEditing({ ...editing, title: e.target.value })} placeholder="Titulo" />
                <input className="min-w-0 rounded-md border border-slate-300 p-2" value={editing.slug || ''} onChange={(e) => setEditing({ ...editing, slug: e.target.value })} placeholder="slug-do-link" />
              </div>
              <textarea className="h-20 w-full rounded-md border border-slate-300 p-2" value={editing.description || ''} onChange={(e) => setEditing({ ...editing, description: e.target.value })} placeholder="Descricao" />
              <div className="grid gap-3 md:grid-cols-2">
                <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700">Brand aplicada: <b>{brand?.name || 'Padrao IntegraIA'}</b></div>
                <div className="rounded-md border border-slate-200 bg-white">
                  <div className="border-b border-slate-100 p-2">
                    <input
                      value={categorySearch}
                      onChange={(e) => setCategorySearch(e.target.value)}
                      className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm outline-none focus:border-blue-400"
                      placeholder="Buscar por categoria ou funil"
                    />
                  </div>
                  <div className="max-h-56 overflow-y-auto p-2">
                    <button
                      type="button"
                      onClick={() => setEditing({ ...editing, defaultCategoriaId: null })}
                      className={`mb-2 w-full rounded-md px-3 py-2 text-left text-sm ${!editing.defaultCategoriaId ? 'bg-blue-50 font-medium text-blue-700' : 'text-slate-700 hover:bg-slate-50'}`}
                    >
                      Nao colocar em funil
                    </button>
                    {Object.keys(groupedCategories).length === 0 ? <div className="px-3 py-2 text-sm text-slate-500">Nenhuma categoria encontrada.</div> : null}
                    {Object.entries(groupedCategories).map(([funnelId, group]) => (
                      <div key={funnelId} className="mb-3 last:mb-0">
                        <div className="px-3 py-1 text-xs font-semibold uppercase tracking-wide text-slate-500">{group.name}</div>
                        <div className="space-y-1">
                          {group.items.map((category) => (
                            <button
                              key={category.id}
                              type="button"
                              onClick={() => setEditing({ ...editing, defaultCategoriaId: category.id })}
                              className={`w-full rounded-md px-3 py-2 text-left text-sm ${editing.defaultCategoriaId === category.id ? 'bg-blue-50 font-medium text-blue-700' : 'text-slate-700 hover:bg-slate-50'}`}
                            >
                              {category.nome}
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <ContactLabelSelector value={editing.defaultTags || []} onChange={(defaultTags) => setEditing({ ...editing, defaultTags })} label="Etiquetas adicionadas ao contato enviado pelo formulario" />
              <input className="w-full rounded-md border border-slate-300 p-2" value={editing.redirectUrl || ''} onChange={(e) => setEditing({ ...editing, redirectUrl: e.target.value })} placeholder="Link de redirecionamento apos enviar (opcional)" />
              <div className="space-y-3">
                {(editing.fields || []).map((field, index) => (
                  <div key={field.id} className="rounded-md border border-slate-200 bg-slate-50 p-3">
                    <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-[minmax(0,1.4fr)_120px_160px_80px_38px]">
                      <input className="min-w-0 rounded-md border border-slate-300 bg-white p-2" value={field.label} onChange={(e) => updateField(index, { label: e.target.value })} placeholder="Pergunta" />
                      <select className="min-w-0 rounded-md border border-slate-300 bg-white p-2" value={field.type} onChange={(e) => updateField(index, { type: e.target.value as any })}>
                        <option value="text">Texto</option><option value="textarea">Longo</option><option value="email">Email</option><option value="phone">Telefone</option><option value="select">Lista</option>
                      </select>
                      <select className="min-w-0 rounded-md border border-slate-300 bg-white p-2" value={field.contactField || ''} onChange={(e) => updateField(index, { contactField: e.target.value as any || null })}>
                        <option value="">Salvar so na resposta</option><option value="nome">Nome</option><option value="telefone">Telefone</option><option value="email">Email</option><option value="observacoes">Observacoes</option><option value="cnpj">CNPJ</option><option value="endereco">Endereco</option>
                      </select>
                      <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={field.required} onChange={(e) => updateField(index, { required: e.target.checked })} />Obrig.</label>
                      <button type="button" onClick={() => setEditing({ ...editing, fields: (editing.fields || []).filter((_, i) => i !== index) })} className="rounded-md border border-red-200 bg-white px-2 text-red-600">X</button>
                    </div>
                    {field.type === 'select' && (
                      <div className="mt-3 rounded-md border border-slate-200 bg-white p-3">
                        <div className="mb-2 flex items-center justify-between gap-2">
                          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Opcoes da lista</p>
                          <button type="button" onClick={() => addFieldOption(index)} className="rounded-md border border-slate-300 px-2.5 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50">+ Opcao</button>
                        </div>
                        <div className="space-y-2">
                          {((field.options || []).length ? field.options || [] : ['']).map((option, optionIndex) => (
                            <div key={optionIndex} className="flex items-center gap-2">
                              <input
                                className="min-w-0 flex-1 rounded-md border border-slate-300 bg-white p-2 text-sm"
                                value={option}
                                onChange={(e) => updateFieldOption(index, optionIndex, e.target.value)}
                                placeholder={`Opcao ${optionIndex + 1}`}
                              />
                              <button
                                type="button"
                                onClick={() => removeFieldOption(index, optionIndex)}
                                disabled={(field.options || []).length <= 1}
                                className="rounded-md border border-red-200 px-2.5 py-2 text-xs font-semibold text-red-600 hover:bg-red-50 disabled:cursor-not-allowed disabled:border-slate-200 disabled:text-slate-300"
                              >
                                X
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <button onClick={() => setEditing({ ...editing, fields: [...(editing.fields || []), makeField()] })} className="rounded-md border border-slate-300 px-3 py-2 text-sm">Adicionar pergunta</button>
                <button onClick={() => save('draft')} className="rounded-md border border-slate-300 px-4 py-2 text-sm">Salvar rascunho</button>
                <button onClick={() => save('published')} className="rounded-md bg-blue-600 px-4 py-2 text-sm text-white">Publicar</button>
                {editing.id && <button onClick={() => removeForm()} className="rounded-md border border-red-200 px-4 py-2 text-sm text-red-600">Excluir formulario</button>}
              </div>
              {publicLink && <div className="rounded-md bg-slate-50 p-3 text-sm"><span className="text-slate-500">Link publico:</span> <a className="break-all text-blue-600" href={publicLink} target="_blank">{publicLink}</a></div>}
            </div>
          </section>
          <div className="xl:sticky xl:top-16 xl:self-start"><FormPreview form={editing} /></div>
        </div>
      )}

      {mode === 'answers' && (
        <section className="rounded-lg border border-slate-200 bg-white p-4 lg:p-5">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 pb-4">
            <div><h2 className="text-lg font-semibold text-slate-900">Respostas - {editing.title}</h2><p className="text-sm text-slate-500">Cada envio fica vinculado ao contato criado ou atualizado.</p></div>
            <button onClick={() => editing.id && loadAnswers(editing as PublicForm).catch((e) => toast.error(e.message))} className="rounded-md border border-slate-300 px-3 py-2 text-sm">Atualizar</button>
          </div>
          {answerData.submissions.length === 0 ? <p className="text-sm text-slate-500">Nenhuma resposta recebida ainda.</p> : null}
          <div className="space-y-3">
            {answerData.submissions.map((submission) => (
              <div key={submission.id} className="rounded-md border border-slate-200 p-4">
                <div className="mb-3 flex flex-wrap items-center justify-between gap-2 text-sm">
                  <div><b>{submission.contact?.nome || 'Contato'}</b> <span className="text-slate-500">{submission.contact?.telefone || ''}</span></div>
                  <div className="text-xs text-slate-500">{new Date(submission.createdAt).toLocaleString()}</div>
                </div>
                <div className="grid gap-2 md:grid-cols-2 xl:grid-cols-3">
                  {answerData.fields.map((field) => <div key={field.id} className="rounded bg-slate-50 p-2 text-sm"><div className="font-medium text-slate-600">{field.label}</div><div className="mt-1 whitespace-pre-wrap text-slate-900">{submission.answers?.[field.id] || '-'}</div></div>)}
                </div>
                {submission.contactId && <a href={`/contatos?search=${encodeURIComponent(submission.contact?.telefone || '')}`} className="mt-3 inline-block text-sm text-blue-600">Abrir contato vinculado</a>}
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

export { FormPreview };
